/**
 * attribution.ts — ?channel/?campaign tags and the ?v landing variant.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.1 (patient journey).
 *
 * WHY: marketing lands patients on `/:clinicSlug?channel=…&campaign=…` and
 * the tags must ride on the Lead and the Assessment so reporting can attribute
 * conversions (R19-safe: enum-ish strings, never PHI). Values are clamped to
 * the contract's 64-character cap so a crafted URL cannot smuggle long text
 * into events. `?v=meta` selects the lean paid-social variant of Step 0.
 */
import type { Attribution } from '../api/types';

/** Max tag length — mirrors `attribution` in gpsclinics-contracts. */
const MAX_TAG_LENGTH = 64;

/** Read a single tag: trimmed, clamped, empty → undefined. */
function readTag(params: URLSearchParams, name: string): string | undefined {
  const raw = params.get(name)?.trim();
  return raw ? raw.slice(0, MAX_TAG_LENGTH) : undefined;
}

/** Parse ?channel/?campaign from a location search string. */
export function parseAttribution(search: string): Attribution | undefined {
  const params = new URLSearchParams(search);
  const channel = readTag(params, 'channel');
  const campaign = readTag(params, 'campaign');
  if (!channel && !campaign) return undefined;
  const attribution: Attribution = {};
  if (channel) attribution.channel = channel;
  if (campaign) attribution.campaign = campaign;
  return attribution;
}

/** Landing variant: `meta` = lean paid-social capture; anything else = default. */
export function parseVariant(search: string): 'default' | 'meta' {
  return new URLSearchParams(search).get('v') === 'meta' ? 'meta' : 'default';
}
